import React, { useState, useEffect } from 'react';
import '../../css/AdminModal.css'; // Створимо стилі для модалки

const EditModal = ({ isOpen, onClose, initialValue, onSave, fieldType = 'text', fieldLabel, brands, models }) => {
    // Для звичайного редагування currentValue - це рядок
    // Для створення категорії currentValue - це об'єкт { name: '', photo: '' }
    // Для створення марки currentValue - це об'єкт { name: '' }
    // Для створення моделі currentValue - це об'єкт { name: '', brandId: '' }
    // Для редагування марки/моделі currentValue - це об'єкт { brandId: '', modelId: '' }
    const [currentValue, setCurrentValue] = useState(initialValue);
    
    // Стан для фільтрованих моделей (для editBrandModel)
    const [filteredModels, setFilteredModels] = useState([]);

    // Оновлюємо значення в модалці, якщо змінились початкові дані
    useEffect(() => {
        if (isOpen) {
            // Переконуємось, що копіюємо об'єкт для створення, щоб уникнути мутацій
            const isCreateType = ['createCategory', 'createBrand', 'createModel', 'editBrandModel'].includes(fieldType);
            setCurrentValue(isCreateType && typeof initialValue === 'object' ? { ...initialValue } : initialValue);
        }
        // Запускаємо ефект ТІЛЬКИ при зміні isOpen або fieldType
    }, [isOpen, fieldType]); // <--- Прибираємо initialValue із залежностей
    
    // Фільтрація моделей при зміні марки (для editBrandModel)
    useEffect(() => {
        if (fieldType === 'editBrandModel' && typeof currentValue === 'object' && currentValue.brandId) {
            // Підтримуємо обидві структури: model.brandId та model.brand.id
            const filtered = models?.filter(model => {
                const modelBrandId = model.brandId || model.brand?.id;
                return modelBrandId === Number(currentValue.brandId);
            }) || [];
            setFilteredModels(filtered);
            // Якщо вибрана модель не належить до нової марки, скидаємо її
            if (currentValue.modelId) {
                const isModelValid = filtered.some(model => model.id === Number(currentValue.modelId));
                if (!isModelValid) {
                    setCurrentValue(prev => ({ ...prev, modelId: '' }));
                }
            }
        } else if (fieldType === 'editBrandModel') {
            setFilteredModels([]);
        }
    }, [fieldType, currentValue?.brandId, models]);

    // Блокування скролу body коли модалка відкрита
    useEffect(() => {
        if (isOpen) {
            // Зберігаємо поточну позицію скролу
            const scrollY = window.scrollY;
            
            // Блокуємо скрол на body і html
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            
            return () => {
                // Відновлюємо скрол
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflow = '';
                document.documentElement.style.overflow = '';
                
                // Повертаємо позицію скролу
                window.scrollTo(0, scrollY);
            };
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const handleSave = () => {
        onSave(currentValue); // Передаємо або рядок, або об'єкт { name, photo }
        onClose();
    };

    // Обробник змін для полів створення категорії
    const handleCreateInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentValue(prev => ({ ...prev, [name]: value }));
    };

    // Визначаємо, що рендерити
    const renderContent = () => {
        if (fieldType === 'createCategory') {
            const nameValue        = typeof currentValue === 'object' && currentValue !== null ? currentValue.name         : '';
            const nameSingularValue= typeof currentValue === 'object' && currentValue !== null ? currentValue.nameSingular : '';
            const slugValue        = typeof currentValue === 'object' && currentValue !== null ? currentValue.slug         : '';
            const photoValue       = typeof currentValue === 'object' && currentValue !== null ? currentValue.photo        : '';
            return (
                <>
                    <div className="modal-input-container">
                        <label htmlFor="categoryName">Назва категорії (множина):</label>
                        <input
                            type="text"
                            id="categoryName"
                            name="name"
                            value={nameValue || ''}
                            onChange={handleCreateInputChange}
                            className="modal-input modal-input-text"
                            autoFocus
                            placeholder="Наприклад: Коробки передач"
                        />
                    </div>
                    <div className="modal-input-container">
                        <label htmlFor="categoryNameSingular">Назва категорії (однина):</label>
                        <input
                            type="text"
                            id="categoryNameSingular"
                            name="nameSingular"
                            value={nameSingularValue || ''}
                            onChange={handleCreateInputChange}
                            className="modal-input modal-input-text"
                            placeholder="Наприклад: Коробка передач"
                        />
                    </div>
                    <div className="modal-input-container">
                        <label htmlFor="categorySlug">Slug (URL):</label>
                        <input
                            type="text"
                            id="categorySlug"
                            name="slug"
                            value={slugValue || ''}
                            onChange={handleCreateInputChange}
                            className="modal-input modal-input-text"
                            placeholder="Наприклад: korobky-peredach"
                        />
                    </div>
                    <div className="modal-input-container">
                        <label htmlFor="categoryPhoto">Фото URL:</label>
                        <input
                            type="text"
                            id="categoryPhoto"
                            name="photo"
                            value={photoValue || ''}
                            onChange={handleCreateInputChange}
                            className="modal-input modal-input-text"
                        />
                    </div>
                </>
            );
        } else if (fieldType === 'createBrand') {
            // Рендеримо одне поле для створення марки
            const nameValue = typeof currentValue === 'object' && currentValue !== null ? currentValue.name : '';
            return (
                <div className="modal-input-container">
                    <label htmlFor="brandName">Назва марки:</label>
                    <input
                        type="text"
                        id="brandName"
                        name="name"
                        value={nameValue || ''}
                        onChange={handleCreateInputChange}
                        className="modal-input modal-input-text"
                        autoFocus
                        placeholder="Наприклад: BMW, Mercedes, Toyota"
                    />
                </div>
            );
        } else if (fieldType === 'createModel') {
            // Рендеримо два поля для створення моделі (назва + селект марки)
            const nameValue = typeof currentValue === 'object' && currentValue !== null ? currentValue.name : '';
            const brandIdValue = typeof currentValue === 'object' && currentValue !== null ? currentValue.brandId : '';
            return (
                <>
                    <div className="modal-input-container">
                        <label htmlFor="modelName">Назва моделі:</label>
                        <input
                            type="text"
                            id="modelName"
                            name="name"
                            value={nameValue || ''}
                            onChange={handleCreateInputChange}
                            className="modal-input modal-input-text"
                            autoFocus
                            placeholder="Наприклад: X5, E-Class, Camry"
                        />
                    </div>
                    <div className="modal-input-container">
                        <label htmlFor="modelBrand">Марка:</label>
                        <select
                            id="modelBrand"
                            name="brandId"
                            value={brandIdValue || ''}
                            onChange={handleCreateInputChange}
                            className="modal-input modal-input-select"
                        >
                            <option value="">Оберіть марку</option>
                            {brands && brands.map(brand => (
                                <option key={brand.id} value={brand.id}>
                                    {brand.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </>
            );
        } else if (fieldType === 'editBrandModel') {
            // Рендеримо два випадаючих списки для редагування марки і моделі
            const brandIdValue = typeof currentValue === 'object' && currentValue !== null ? currentValue.brandId : '';
            const modelIdValue = typeof currentValue === 'object' && currentValue !== null ? currentValue.modelId : '';
            return (
                <>
                    <div className="modal-input-container">
                        <label htmlFor="editBrand">Марка автомобіля (опціонально):</label>
                        <select
                            id="editBrand"
                            name="brandId"
                            value={brandIdValue || ''}
                            onChange={handleCreateInputChange}
                            className="modal-input modal-input-select"
                        >
                            <option value="">-- Не вибрано --</option>
                            {brands && brands.map(brand => (
                                <option key={brand.id} value={brand.id}>
                                    {brand.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="modal-input-container">
                        <label htmlFor="editModel">Модель автомобіля (опціонально):</label>
                        <select
                            id="editModel"
                            name="modelId"
                            value={modelIdValue || ''}
                            onChange={handleCreateInputChange}
                            className="modal-input modal-input-select"
                            disabled={!brandIdValue}
                        >
                            <option value="">-- Не вибрано --</option>
                            {filteredModels.map(model => (
                                <option key={model.id} value={model.id}>
                                    {model.name}
                                </option>
                            ))}
                        </select>
                        {!brandIdValue && (
                            <small style={{ color: '#666', fontSize: '12px' }}>
                                Спочатку оберіть марку
                            </small>
                        )}
                    </div>
                </>
            );
        } else {
            // Рендеримо одне поле для редагування
            const InputComponent = fieldType === 'textarea' ? 'textarea' : 'input';
            return (
                <div className="modal-input-container">
                     <InputComponent
                        type={fieldType === 'textarea' ? undefined : fieldType}
                        value={currentValue === null || currentValue === undefined ? '' : currentValue}
                        onChange={e => setCurrentValue(e.target.value)}
                        className={`modal-input modal-input-${fieldType}`}
                        rows={fieldType === 'textarea' ? 5 : undefined}
                        autoFocus
                    />
                </div>
            );
        }
    };

    return (
        <div className="admin-modal-backdrop" onClick={onClose}> {/* Закриття по кліку на фон */}
            <div className="admin-modal-content" onClick={e => e.stopPropagation()}> {/* Зупиняємо спливання, щоб клік по модалці не закривав її */}
                <h3>{fieldLabel}</h3> {/* Заголовок береться з CategoriesAdmin */} 
                {renderContent()} {/* Рендеримо або одне, або два поля */} 
                <div className="modal-actions">
                    <button onClick={handleSave} className="modal-save-button">Зберегти</button>
                    <button onClick={onClose} className="modal-cancel-button">Скасувати</button>
                </div>
            </div>
        </div>
    );
};

export default EditModal; 